import React, { useState, useRef, useEffect } from 'react'
import onResize from 'simple-element-resize-detector'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

const SimpleMapExample = (props) => {
	const { direction, coordinates, polyline, accidentTime, zoneData } = props;
	const { t } = useTranslation('translation', { keyPrefix: 'components.advancedPlayer.simpleMapExampleMap' });

	let mapRef = useRef(null);
	const [mapState, setMapState] = useState({});
	const [map, setMap] = useState(null);
	const [mapUI, setMapUI] = useState(null);
	const [polylineObj, setPolylineObj] = useState(null);

	useEffect(() => {
		if (!map) {
			// instantiate a platform, default layers and a map as usual
			const platform = new H.service.Platform({
				apikey: process.env.REACT_APP_HERE_MAPS_API_KEY
			});
			const layers = platform.createDefaultLayers();
			const MAP = new H.Map(
				mapRef,
				layers.vector.normal.map,
				{
					// center: { lat: -34.397, lng: 150.644 },
					zoom: 1.2,
					pixelRatio: window.devicePixelRatio || 1
				},
			);

			onResize(mapRef, (a, p, c) => {
				MAP.getViewPort().resize();
			});
			// attach the listener
			MAP.addEventListener('mapviewchange', handleMapViewChange);
			// add the interactive behaviour to the map
			new H.mapevents.Behavior(new H.mapevents.MapEvents(MAP));
			// Create the default UI components
			const ui = H.ui.UI.createDefault(MAP, layers);
			ui.getControl('mapsettings').setAlignment('top-left');
			ui.getControl('zoom').setAlignment('right-top');

			setMap(MAP);
			setMapUI(ui);
		}

		return () => {
			if (map) map.removeEventListener('mapviewchange', handleMapViewChange);
		}
	}, [])

	useEffect(() => {
		if (map) {
			map.setCenter(coordinates);
			if (coordinates.lat && coordinates.lng && Math.ceil(map.getZoom() * 10) / 10 === 1.2) map.setZoom(18);

			if (polyline.length && !polylineObj) {
				const polylineOptions = {
					strokeColor: '#FF0000',
					lineWidth: 3,
				};

				if (polyline.length === 1) {
					polyline.push(polyline[0]);
				}

				const lineString = new H.geo.LineString();
				polyline.map(elm => { lineString.pushPoint({ lat: elm.lat, lng: elm.lng }) });
				const polylineElm = new H.map.Polyline(lineString, { style: polylineOptions });
				map.addObject(polylineElm);
				setPolylineObj(polylineElm);
			}

			const marker = map.getObjects().filter(item => (item.type === H.map.Object.Type.DOM_MARKER))[0],
				divIcon = document.createElement('div'),
				img = document.createElement('img');

			divIcon.setAttribute('class', 'map-narker');
			img.setAttribute('src', '/images/navigation.png');
			img.style.transform = `translate(-50%, -50%) rotate(${direction + 'deg'})`;
			divIcon.appendChild(img);

			if (!marker) {
				map.addObject(new H.map.DomMarker(coordinates, { icon: new H.map.DomIcon(divIcon) }));
			} else {
				marker.setGeometry(coordinates);
				marker.setIcon(new H.map.DomIcon(divIcon));
			}
		}
	}, [coordinates]);

	useEffect(() => {
		if (!map || !zoneData) return;

		const k = (v, radius) => Math.round(v / 2 + radius * v / 10);
		const zoneGroup = new H.map.Group();
		zoneGroup.setRemoteId('zoneGroup');

		if (zoneData.shape_type === 'POLYGON' && zoneData.shape_points.length >= 3) {
			const strip = new H.geo.LineString();
			zoneData.shape_points.forEach(point => {
				strip.pushLatLngAlt(Number(point.lat), Number(point.lng), 0);
			});
			const geoPolygon = new H.geo.Polygon(strip);

			const polygon = new H.map.Polygon(geoPolygon, {
				style: { fillColor: `${zoneData.shape_color}55`, lineWidth: 0 }
			});

			const centerLat = zoneData.shape_points.reduce((sum, p) => sum + Number(p.lat), 0) / zoneData.shape_points.length;
			const centerLng = zoneData.shape_points.reduce((sum, p) => sum + Number(p.lng), 0) / zoneData.shape_points.length;
			const centerPosition = { lat: centerLat, lng: centerLng };

			const div = document.createElement('div');
			const label = document.createElement('label');
			div.setAttribute('class', 'markerZone');
			label.style.fontSize = '18px';
			label.style.marginTop = '25px';
			if (zoneData.zone_name) label.innerText = zoneData.zone_name;
			div.appendChild(label);
			const textLabel = new H.map.DomMarker(centerPosition, { icon: new H.map.DomIcon(div) });
			textLabel.setData({ domElement: div });

			const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="55" viewBox="0 0 24 24"><path fill="${zoneData.shape_color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
			const pinMarker = new H.map.Marker(centerPosition, {
				icon: new H.map.Icon(svgIcon, { size: { w: 38, h: 55 } })
			});

			zoneGroup.addObjects([polygon, pinMarker, textLabel]);
		} else if (zoneData.shape_type === 'CIRCLE') {
			const position = { lat: zoneData.shape_points[0].lat, lng: zoneData.shape_points[0].lng };
			const radius = zoneData.circle_radius;

			const div = document.createElement('div');
			const label = document.createElement('label');
			div.setAttribute('class', 'markerZone');
			label.style.fontSize = `${k(18, radius)}px`;
			label.style.marginTop = '25px';
			if (zoneData.zone_name) label.innerText = zoneData.zone_name;
			div.appendChild(label);
			const domIcon = new H.map.DomIcon(div);
			const textLabel = new H.map.DomMarker(position, { icon: domIcon });
			textLabel.setData({ domElement: div });

			const size = { w: k(38, radius), h: k(55, radius) };
			const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="${size.w}" height="${size.h}" viewBox="0 0 24 24"><path fill="${zoneData.shape_color}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
			const marker = new H.map.Marker(position, {
				icon: new H.map.Icon(svgIcon, { size })
			});

			const circle = new H.map.Circle(position, radius * 1609.34, {
				style: { fillColor: `${zoneData.shape_color}55`, lineWidth: 0 }
			});

			zoneGroup.addObjects([marker, textLabel, circle]);
		}
		map.addObject(zoneGroup);

		return () => {
			if (zoneGroup && map) {
				map.removeObject(zoneGroup);
			}
		};
	}, [map, zoneData]);

	useEffect(() => {
		if (!map) return;
		const zoneGroup = map.getObjects().find(obj => obj instanceof H.map.Group && obj.getRemoteId() === 'zoneGroup');
		if (!zoneGroup) return;
		zoneGroup.getObjects().forEach(obj => {
			if (obj instanceof H.map.DomMarker) {
				obj.setVisibility(mapState.zoom >= 10);
			}
		});
	}, [mapState]);

	const addLocation = () => {
		axios.get(`https://revgeocode.search.hereapi.com/v1/revgeocode?at=${coordinates.lat},${coordinates.lng}&lang=en-US&apiKey=${process.env.REACT_APP_HERE_MAPS_API_KEY}`)
			.then(async (res) => {
				if (res.status === 200) {
					const address = await res.data.items[0].address.label || t('textUnknown');

					const content = `<div style="background-xolor: white; padding: 0; margin: 0; width: 275px">
						<div style="font-size: 10px; font-color: #08233B">
							<div style="margin: 0 10px">
								<p style="font-weight: bold; margin: 0">${t('titleTimeCollision')}</p>
								<p style="margin: 0">${accidentTime}</p>
							</div>
							<div style="margin: 10px">
								<p style="font-weight: bold; margin: 0">${t('titleLocation')}</p>
								<p style="margin: 0">${address}</p>
							</div>
						</div>
					</div>`;

					const bubbleElm = new H.ui.InfoBubble(coordinates, { content });
					bubbleElm.addClass("infoTripBubble");
					mapUI.addBubble(bubbleElm);

					mapUI.getControl('zoom').setVisibility(false);
					mapUI.getControl('scalebar').setVisibility(false);
					mapUI.getControl('mapsettings').setVisibility(false);
				};
			});
	};

	const handleMapViewChange = (e) => {
		if (e.newValue && e.newValue.lookAt) {
			const lookAt = e.newValue.lookAt;
			// adjust precision
			const lat = Math.trunc(lookAt.position.lat * 1E7) / 1E7;
			const lng = Math.trunc(lookAt.position.lng * 1E7) / 1E7;
			const zoom = Math.trunc(lookAt.zoom * 1E2) / 1E2;

			setMapState({ lat, lng, zoom })
		}
	}

	return (
		<div
			id="map"
			style={{ position: 'relative', width: '100%', height: '100%' }}
			ref={(c) => { mapRef = c }}
		>
			{!!accidentTime && !mapUI.getBubbles().length && addLocation()}
		</div>
	)
};

export default SimpleMapExample;
